import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, Search, Check, X, Loader, Mail } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * UserSelector - Search and select existing users from the database
 * Returns { uuid, email, first_name, last_name, profile_img, handle }
 */
const UserSelector = ({ 
  value,           // { uuid, email, first_name, last_name, ... } or null
  onChange,        // (selection) => void
  placeholder = "Search users by name, email, or handle...",
  label = "User"
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search users in database
  const searchUsers = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('uuid, email, first_name, last_name, nickname, handle, profile_img')
        .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%`)
        .order('first_name')
        .limit(10);

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      searchUsers(searchQuery);
    }, 300),
    [searchUsers]
  );

  // Trigger search when query changes
  useEffect(() => {
    if (query.trim().length >= 2) {
      debouncedSearch(query);
    } else {
      setUsers([]);
    }
  }, [query, debouncedSearch]);

  // Handle selecting a user
  const handleSelectUser = (user) => {
    onChange({
      uuid: user.uuid,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      nickname: user.nickname,
      handle: user.handle,
      profile_img: user.profile_img
    });
    setQuery('');
    setIsOpen(false);
  };

  // Clear selection
  const handleClear = () => {
    onChange(null);
    setQuery('');
  };

  // Get display name for user
  const getDisplayName = (user) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    if (user.nickname) return user.nickname;
    if (user.handle) return `@${user.handle}`;
    return user.email;
  };

  const showDropdown = isOpen && (users.length > 0 || loading);

  return (
    <div className="user-selector">
      {value ? (
        // Selected state
        <div className="user-selected">
          <div className="user-selected-info">
            {value.profile_img ? (
              <img src={value.profile_img} alt="" className="user-avatar" />
            ) : (
              <div className="user-avatar-placeholder">
                <User size={16} />
              </div>
            )}
            <div className="user-details">
              <span className="user-name">{getDisplayName(value)}</span>
              {value.email && (
                <span className="user-email">{value.email}</span>
              )}
              {value.handle && (
                <span className="user-handle">@{value.handle}</span>
              )}
            </div>
          </div>
          <button type="button" className="clear-btn" onClick={handleClear}>
            <X size={16} />
          </button>
        </div>
      ) : (
        // Search state
        <div className="user-search-container">
          <div className="user-search-input">
            <Search size={16} className="search-icon" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              autoComplete="off"
            />
            {loading && (
              <Loader size={16} className="loading-icon spinner" />
            )}
          </div>

          {showDropdown && (
            <div ref={dropdownRef} className="user-dropdown">
              {users.length > 0 && (
                <div className="dropdown-section">
                  {users.map((user) => (
                    <button
                      key={user.uuid}
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleSelectUser(user)}
                    >
                      {user.profile_img ? (
                        <img src={user.profile_img} alt="" className="item-avatar" />
                      ) : (
                        <div className="item-avatar-placeholder">
                          <User size={14} />
                        </div>
                      )}
                      <div className="item-content">
                        <span className="item-name">{getDisplayName(user)}</span>
                        <span className="item-email">{user.email}</span>
                      </div>
                      <Check size={14} className="item-check" />
                    </button>
                  ))}
                </div>
              )}

              {/* Loading state */}
              {loading && users.length === 0 && (
                <div className="dropdown-loading">
                  <Loader size={16} className="spinner" />
                  <span>Searching users...</span>
                </div>
              )}

              {/* No results */}
              {!loading && query.trim().length >= 2 && users.length === 0 && (
                <div className="dropdown-empty">
                  <span>No users found</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSelector;
